import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ShoppingBagIcon,
  CurrencyDollarIcon,
  TruckIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

function OrdersDashboard() {
  const [selectedTimeframe, setSelectedTimeframe] = useState('7d');
  const [selectedMarketplace, setSelectedMarketplace] = useState('all');

  const metrics = [
    {
      name: 'Total Orders',
      value: '1,234',
      change: '+12%',
      changeType: 'increase',
      icon: ShoppingBagIcon
    },
    {
      name: 'Revenue',
      value: '$45,678',
      change: '+8%',
      changeType: 'increase',
      icon: CurrencyDollarIcon
    },
    {
      name: 'Fulfillment Rate',
      value: '98.5%',
      change: '+2.1%',
      changeType: 'increase',
      icon: TruckIcon
    },
    {
      name: 'Processing Time',
      value: '1.2 days',
      change: '-0.3',
      changeType: 'decrease',
      icon: ClockIcon
    }
  ];

  const recentOrders = [
    {
      id: 'ORD-001',
      customer: 'Ahmed Hassan',
      marketplace: 'Amazon UAE',
      total: 299.99,
      status: 'Processing',
      date: '2024-03-15',
      items: [
        { name: 'Wireless Headphones', quantity: 1, price: 199.99 },
        { name: 'Phone Case', quantity: 2, price: 49.99 }
      ]
    },
    {
      id: 'ORD-002',
      customer: 'Sarah Ahmed',
      marketplace: 'Noon',
      total: 499.99,
      status: 'Shipped',
      date: '2024-03-14',
      items: [
        { name: 'Smart Watch', quantity: 1, price: 499.99 }
      ]
    },
    {
      id: 'ORD-003',
      customer: 'Mohammed Ali',
      marketplace: 'Carrefour',
      total: 149.99,
      status: 'Delivered',
      date: '2024-03-13',
      items: [
        { name: 'Bluetooth Speaker', quantity: 1, price: 149.99 }
      ]
    }
  ];

  const alerts = [
    {
      type: 'warning',
      message: 'Order ORD-001 requires immediate attention',
      action: 'Review'
    },
    {
      type: 'info',
      message: 'New bulk order received from Noon',
      action: 'View'
    },
    {
      type: 'warning',
      message: 'Delayed shipment for order ORD-002',
      action: 'Track'
    }
  ];

  const getStatusColor = (status) => {
    const colors = {
      'Processing': 'bg-yellow-100 text-yellow-800',
      'Shipped': 'bg-blue-100 text-blue-800',
      'Delivered': 'bg-green-100 text-green-800',
      'Cancelled': 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="p-6">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Orders Overview</h1>
        <p className="mt-1 text-sm text-gray-500">
          Monitor and manage your marketplace orders
        </p>
      </div>

      {/* Filters */}
      <div className="mb-6 flex space-x-4">
        <select
          value={selectedTimeframe}
          onChange={(e) => setSelectedTimeframe(e.target.value)}
          className="rounded-lg border-gray-300 text-sm"
        >
          <option value="7d">Last 7 days</option>
          <option value="30d">Last 30 days</option>
          <option value="90d">Last 90 days</option>
        </select>

        <select
          value={selectedMarketplace}
          onChange={(e) => setSelectedMarketplace(e.target.value)}
          className="rounded-lg border-gray-300 text-sm"
        >
          <option value="all">All Marketplaces</option>
          <option value="amazon">Amazon UAE</option>
          <option value="noon">Noon</option>
          <option value="carrefour">Carrefour</option>
        </select>
      </div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4 mb-8">
        {metrics.map((metric) => (
          <div
            key={metric.name}
            className="bg-white rounded-xl p-6 shadow-sm"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="p-2 bg-[#0049ac]/10 rounded-lg">
                  <metric.icon className="h-6 w-6 text-[#0049ac]" />
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-600">{metric.name}</p>
                  <p className="text-2xl font-semibold text-gray-900">{metric.value}</p>
                </div>
              </div>
              {metric.change && (
                <div className={`flex items-center ${
                  metric.changeType === 'increase' ? 'text-green-600' : 
                  metric.changeType === 'decrease' ? 'text-red-600' : 
                  'text-gray-600'
                }`}>
                  <svg
                    className={`h-4 w-4 mr-1 ${
                      metric.changeType === 'decrease' ? 'rotate-180' : ''
                    }`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
                  </svg>
                  <span className="text-sm font-medium">{metric.change}</span>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Recent Orders */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-xl shadow-sm">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900">Recent Orders</h2>
                <Link
                  to="/orders"
                  className="text-sm font-medium text-[#0049ac] hover:text-[#0049ac]/90"
                >
                  View All
                </Link>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Order ID</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Marketplace</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {recentOrders.map((order) => (
                    <tr key={order.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {order.id}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {order.customer}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {order.marketplace}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(order.status)}`}>
                          {order.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {order.date}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                        ${order.total.toFixed(2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Alerts */}
        <div>
          <div className="bg-white rounded-xl shadow-sm">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Alerts</h2>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                {alerts.map((alert, index) => (
                  <div
                    key={index}
                    className="flex items-start space-x-3 p-4 rounded-lg bg-gray-50"
                  >
                    <div className="flex-shrink-0">
                      {alert.type === 'warning' ? (
                        <ExclamationTriangleIcon className="h-5 w-5 text-yellow-500" />
                      ) : (
                        <CheckCircleIcon className="h-5 w-5 text-blue-500" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-900">{alert.message}</p>
                    </div>
                    <div className="flex-shrink-0">
                      <button className="text-sm font-medium text-[#0049ac] hover:text-[#0049ac]/90">
                        {alert.action}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default OrdersDashboard;